import { cn } from '@/lib/utils'
import { useMemo, useState } from 'react'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { useIsAuthor } from '@/features/auth/useIsAuthor'
import { useGetInstructionCountsQuery } from '@/features/instructions/instructionsApi'
import { useGetAllMyInstructionsQuery } from '@/features/instructions/myInstructionsApi'
import { useRevealOnScroll } from '@/lib/useRevealOnScroll'
import type { Instruction } from '@/features/instructions/types'
import { CreateInstructionTile } from './CreateInstructionTile'
import { ListFilters, type FilterId, type FilterCounts } from './ListFilters'
import { InstructionTile } from './InstructionTile'
import { InstructionTileSkeleton } from './InstructionTileSkeleton'
import { EmptyState } from './EmptyState'
import { CARD_GRID_CLASS, CARD_PAGE_PX_CLASS } from '@/components/catalog/cardGrid'

const accessTypeFromFilter = (filter: FilterId): 'private' | 'public' | 'link' | undefined => {
  if (filter === 'drafts') return 'private'
  if (filter === 'public') return 'public'
  if (filter === 'link') return 'link'
  return undefined
}

export const InstructionsList = () => {
  const { isAuthor } = useIsAuthor()

  const [filter, setFilter] = useState<FilterId>('all')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)

  // Все статьи разом: фильтр, поиск и счётчики — по всем, а не по первой странице.
  const myInstructions = useGetAllMyInstructionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  const { data: countsData } = useGetInstructionCountsQuery()

  const items: Instruction[] = useMemo(() => {
    const access = accessTypeFromFilter(filter)
    const needle = debouncedSearch.trim().toLowerCase()
    return (myInstructions.data ?? [])
      .filter((i) => !access || i.accessType === access)
      .filter((i) => !needle || i.title.toLowerCase().includes(needle))
      .sort((a, b) => b.id - a.id) // сверху самые свежие
  }, [myInstructions.data, filter, debouncedSearch])

  // Порциями при прокрутке; смена фильтра или поиска — снова с первой порции.
  const { shown, hasMore, sentinelRef } = useRevealOnScroll(
    items.length,
    `${filter}|${debouncedSearch}`,
  )

  const isLoading = myInstructions.isLoading

  const showEmpty = !isLoading && items.length === 0
  // Ничего не создано вовсе (счётчики ответили нулями): ни поиска, ни фильтров — только
  // маскот по центру, как на «Мои курсы». Пока счётчики не приехали, фильтры на месте.
  const hasAnything = !countsData || countsData.all > 0

  const counts: FilterCounts = countsData
    ? {
        all: countsData.all,
        drafts: countsData.private,
        public: countsData.public,
        link: countsData.link,
      }
    : {}

  return (
    <div className={cn('relative z-10 py-4 md:py-6', CARD_PAGE_PX_CLASS)}>
      <div className="h-2" />
      {hasAnything ? (
        <div className="mb-8">
          <ListFilters
            active={filter}
            onChange={setFilter}
            search={search}
            onSearchChange={setSearch}
            counts={counts}
          />
        </div>
      ) : null}
      {isLoading ? (
        <div className={CARD_GRID_CLASS}>
          {Array.from({ length: 8 }).map((_, i) => (
            <InstructionTileSkeleton key={i} />
          ))}
        </div>
      ) : null}
      {showEmpty ? (
        // Пустота по центру свободной области страницы, как на «Мои курсы»
        <div
          className={cn(
            'flex items-center justify-center',
            hasAnything ? 'min-h-[calc(100dvh-22rem)]' : 'min-h-[calc(100dvh-10rem)]',
          )}
        >
          <EmptyState filter={filter} isAuthor={isAuthor} />
        </div>
      ) : null}
      {!isLoading && !showEmpty ? (
        <div className={CARD_GRID_CLASS}>
          {isAuthor && !search.trim() ? <CreateInstructionTile /> : null}
          {items.slice(0, shown).map((instruction) => (
            <InstructionTile key={instruction.id} instruction={instruction} />
          ))}
        </div>
      ) : null}
      {!isLoading && hasMore ? <div ref={sentinelRef} aria-hidden className="h-px" /> : null}
    </div>
  )
}
